import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface HeroImageFieldProps {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp,image/gif";

export const HeroImageField: React.FC<HeroImageFieldProps> = ({
  file,
  onChange,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file === null) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-foreground">
        Hero image
      </label>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.files?.[0];
          onChange(next ?? null);
          event.target.value = "";
        }}
      />
      {previewUrl ? (
        <div className="relative overflow-hidden rounded-2xl bg-neutral-200">
          <img
            src={previewUrl}
            alt="Project hero preview"
            className="h-36 w-full object-cover"
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(null)}
            className="absolute right-2 top-2 rounded-full bg-black/55 p-1.5 text-white hover:bg-black/70"
            aria-label="Remove hero image"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex h-36 w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-neutral-100 text-muted-foreground transition-colors hover:border-bauhaus-red hover:text-bauhaus-red",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <ImagePlus className="h-6 w-6" />
          <span className="text-sm">Upload a thumbnail or hero photo</span>
        </button>
      )}
    </div>
  );
};
